import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { toast } from 'react-hot-toast';

export interface WorkoutExerciseItem {
  title: string;
  setsCount: number;
  repsOrDurationLabel: string;
  formScore: number | null;
  formLabel: string;
  status: string;
  image_male_url?: string;
  image_female_url?: string;
  details?: {
    bodyTracking?: { label: string; value: string; status: string }[];
    aiObservations?: string;
  };
}

export interface ClientWorkoutDetailsData {
  durationMin: number;
  exercisesCount: number;
  caloriesEst: number;
  dateLabel: string;
  ymd?: string;
  selectTime?: string;
  workoutExercises?: WorkoutExerciseItem[];
  performanceOverview?: {
    overallScore: number;
    greatCount: number;
    goodCount: number;
    badCount: number;
    needsFocusCount?: number;
    skippedCount: number;
    colorHex: string;
  };
  primaryGoal?: string;
  secondaryGoals?: string[];
  clientBodyProfile?: string;
  muscleGroups?: string[];
  workoutMetrics?: {
    totalReps: number;
    volume: number;
    restTimeSecs: number;
    weightUnit: string;
  };
  aiObservations?: string;
}

const fetchWorkoutDetails = async (
  clientId: string,
  workoutId: string,
): Promise<ClientWorkoutDetailsData> => {
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_CLIENT_WORKOUT_DETAILS}?clientId=${clientId}&workoutId=${workoutId}`;
  try {
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const json = await response.json();
    if (json.status === 'ok') {
      return json.data;
    } else {
      throw new Error(json.error || 'Failed to fetch workout details');
    }
  } catch (error: any) {
    toast.error(error.message || 'Error fetching workout details');
    throw error;
  }
};

export const useGetClientWorkoutDetails = ({
  clientId,
  workoutId,
}: {
  clientId: string;
  workoutId: string;
}) => {
  return useQuery({
    queryKey: ['clientWorkoutDetails', clientId, workoutId],
    queryFn: () => fetchWorkoutDetails(clientId, workoutId),
    enabled: !!clientId && !!workoutId,
    staleTime: 5 * 60 * 1000,
  });
};
